/**
 * Analytics Portal V1 — the Personal Profile metric map (Phase 11, plan §4.3).
 *
 * Every entry names a key the Phase 8 `GET /api/analytics/profile` payload may contain. Nothing
 * here computes or derives a value: a card renders only when `kpis[key]` exists, so a metric the
 * product does not have (profile shares, profile copy link, intro-video plays) can never appear
 * (C4). Search CTR is deliberately excluded from V1 rendering (amendment A11-2).
 */
export const PROFILE_HEADLINE = [
  { key: "visits", label: "Profile visits", hint: "One visit per session." },
  { key: "unique_visitors", label: "Unique visitors" },
  { key: "returning_visitors", label: "Returning visitors" },
  { key: "engagement_rate", label: "Engagement rate", suffix: "%" },
];

export const PROFILE_GROUPS = [
  {
    id: "discovery",
    title: "Discovery",
    description: "How people found your profile.",
    metrics: [
      { key: "search_impressions", label: "Search impressions" },
      { key: "search_clicks", label: "Search clicks" },
      { key: "unique_search_clickers", label: "Unique search clickers" },
      { key: "directory_impressions", label: "Directory impressions" },
      { key: "directory_clicks", label: "Directory clicks" },
      { key: "unique_directory_clickers", label: "Unique directory clickers" },
    ],
  },
  {
    id: "followers",
    title: "Followers & fans",
    description: "Current totals are live; losses and net change are measured from instrumentation.",
    metrics: [
      { key: "followers_current", label: "Followers now" },
      { key: "followers_added", label: "Followers gained" },
      { key: "followers_lost", label: "Followers lost" },
      { key: "followers_net", label: "Net followers" },
      { key: "fans_current", label: "Fans now" },
      { key: "fans_added", label: "Fans gained" },
      { key: "fans_lost", label: "Fans lost" },
      { key: "fans_net", label: "Net fans" },
    ],
  },
  {
    id: "bookmarks",
    title: "Bookmarks",
    metrics: [
      { key: "bookmarks_current", label: "Bookmarks now" },
      { key: "bookmarks_added", label: "Bookmarks added" },
      { key: "bookmarks_removed", label: "Bookmarks removed" },
      { key: "bookmarks_net", label: "Net bookmarks" },
    ],
  },
  {
    id: "relationship",
    title: "Relationships & requests",
    description: "A meeting request is the primary conversion for a personal profile.",
    metrics: [
      { key: "connections_requested", label: "Connection requests" },
      { key: "connections_accepted", label: "Connections accepted" },
      { key: "meeting_requests", label: "Meeting requests" },
      { key: "unique_converters", label: "Unique converters" },
      { key: "conversion_rate", label: "Conversion rate", suffix: "%" },
    ],
  },
  {
    id: "messaging",
    title: "Questions & conversations",
    description: "Counts and metadata only — no question, answer or message content.",
    metrics: [
      { key: "questions", label: "Questions received" },
      { key: "questions_answered", label: "Questions answered" },
      { key: "conversations_started", label: "Conversations started" },
      { key: "unique_conversation_starters", label: "Unique conversation starters" },
    ],
  },
  {
    id: "clicks",
    title: "Link clicks",
    description: "Outbound covers website, contact and social links; internal covers in-platform links. "
      + "Only the link kind is recorded, never a full address.",
    metrics: [
      { key: "outbound_clicks", label: "Outbound link clicks" },
      { key: "internal_clicks", label: "Internal link clicks" },
    ],
  },
  {
    id: "engagement",
    title: "Chatter & engagement",
    metrics: [
      { key: "engagement_actions", label: "Engagement actions" },
      { key: "unique_engagers", label: "Unique engagers" },
      { key: "comments", label: "Comments" },
      { key: "reactions", label: "Reactions" },
      { key: "rechats", label: "Re-chats" },
      { key: "post_views", label: "Chatter post views" },
    ],
  },
];
